import {
  DataWarehouseConnection,
  WorkflowCodeType,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  Workflow as WorkflowEngine
} from '@cartodb/workflows-engine'
import { WorkflowNodeStatus, WorkflowsExecutionStatus } from '../../../enums'
import { JobStatus } from '../../../../jobs/types'
import { Workflow, WorkflowNodeModel } from '../../../types'

export async function getCompleteNodeDetails(
  workflowEngine: WorkflowEngine,
  dataWarehouseConnection: DataWarehouseConnection,
  envVars: WorkflowEnvVars,
  workflowState: WorkflowsExecutionStatus,
  error: JobStatus['error'],
  failedComponent: string | null,
  workflow: Workflow,
  schemas?: WorkflowTableSchemas
) {
  const workflowParams = workflowEngine.componentValues
  const orderedComponents: Array<string> = workflowEngine.getComponentsInRunningOrder()
  const nodeDetails: Array<WorkflowNodeModel> = []

  const failed = error && Object.keys(error).length
  const errorMessage = error?.msg
  let state: WorkflowNodeStatus = WorkflowNodeStatus.Success
  let nodeError: string | undefined
  let alreadyFailed = false
  for (const componentName of orderedComponents) {
    const component = workflowEngine.components[componentName]
    const componentParams = workflowParams[componentName]
    const componentResolvedParams = workflowEngine.resolveParameterValues({
      connection: dataWarehouseConnection,
      codeType: WorkflowCodeType.Full
    })[componentName]

    let outputs: Record<string, string> = {}
    try {
      const errors = component.checkInputValues({
        params: componentParams,
        resolvedParamValues: componentResolvedParams,
        connection: dataWarehouseConnection,
        dryRun: false,
        schemas,
        variables: workflow.config.variables
      })
      if (errors?.length) {
        state =
          workflowState === WorkflowsExecutionStatus.Running
            ? WorkflowNodeStatus.Running
            : WorkflowNodeStatus.Unexecutable
        nodeError = errors?.[0]?.message
        alreadyFailed = true
      } else if (workflowState === WorkflowsExecutionStatus.Running) {
        state = WorkflowNodeStatus.Running
      } else if (failed && [null, undefined, componentName].includes(failedComponent)) {
        alreadyFailed = true
        state = WorkflowNodeStatus.Failure
        nodeError = componentName === failedComponent ? errorMessage : undefined
      } else if (alreadyFailed) {
        state = WorkflowNodeStatus.Unexecuted
        nodeError = undefined
      } else {
        state = WorkflowNodeStatus.Success
        nodeError = undefined
      }
      if (!errors?.length) {
        outputs = await component.getOutputTables({
          workflow: workflowEngine,
          params: componentResolvedParams,
          connection: dataWarehouseConnection,
          codeType: workflow?.config?.useCache ? WorkflowCodeType.Full : WorkflowCodeType.FullNoCache,
          componentName
        })
      }
    } catch (e) {
      state =
        workflowState === WorkflowsExecutionStatus.Running
          ? WorkflowNodeStatus.Running
          : WorkflowNodeStatus.Unexecutable
      nodeError = `${e}`
      alreadyFailed = true
    }

    const outputTables = Object.keys(outputs).map((outputName) => ({
      name: outputName,
      tableFqn: outputs[outputName]
    }))
    nodeDetails.push({
      name: componentName,
      title: component.metadata.title,
      error: nodeError,
      state: state,
      outputTables: outputTables
    })
  }
  return nodeDetails
}
